<onpoint-credentials-form>
  <div class="flex flex-col gap-4">
    <div>
      <h3 id="onpoint-form-heading" class="text-lg font-bold">
        {props.isEditMode ? locale?.t('admin_onpoint_title_update') : locale?.t('admin_onpoint_title_config')}
      </h3>
      <p class="text-sm text-gray-500 font-semibold mt-1">
        {props.isEditMode ? locale?.t('admin_onpoint_subtitle_update') : locale?.t('admin_onpoint_subtitle_config')}
      </p>
    </div>

    <!-- The account requirement is the most common setup failure, so it leads. -->
    <div class="alert alert-info text-sm">
      <i class="icon-info" aria-hidden="true"></i>
      <span>{locale?.t('admin_onpoint_sa_requirement')}</span>
    </div>

    <div class="form-control">
      <label class="label" for="onpoint-api-url">
        <span class="label-text font-semibold">
          {locale?.t('admin_onpoint_apiurl')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <input
        id="onpoint-api-url"
        type="url"
        class="input input-bordered w-full"
        placeholder="https://customer.onpointdigital.com/api"
        value="{state.apiBaseUrl}"
        oninput="{(e) => onField('apiBaseUrl', e)}"
        required
      />
      <p class="text-xs text-gray-500 mt-1">{locale?.t('admin_onpoint_apiurl_help')}</p>
    </div>

    <div class="form-control">
      <label class="label" for="onpoint-username">
        <span class="label-text font-semibold">
          {locale?.t('admin_onpoint_username')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <!-- Prefilled on an edit from the stored credential (METALARK-1166) so an admin can
           confirm which API user the connection was set up as. Not a secret, unlike the
           password. Blank still means "keep the stored one" — which is what this falls
           back to if the lookup returns nothing. -->
      <input
        id="onpoint-username"
        type="text"
        class="input input-bordered w-full"
        autocomplete="off"
        placeholder="{props.isEditMode ? state.secretMask : ''}"
        value="{state.username}"
        oninput="{(e) => onField('username', e)}"
        required="{!props.isEditMode}"
      />
      <p if="{props.isEditMode && !state.username}" class="text-xs text-gray-600 mt-1 flex items-center gap-1">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_lms_secret_stored_help')}
      </p>
    </div>

    <div class="form-control">
      <label class="label" for="onpoint-password">
        <span class="label-text font-semibold">
          {locale?.t('admin_onpoint_password')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <!-- In edit mode the box carries a masked PLACEHOLDER, not a value: a stored
           password cannot be read back, and putting bullets in `value` would send them to
           the server as the credential the moment someone saved without retyping. Empty
           means "keep what is stored"; typing replaces it. -->
      <input
        id="onpoint-password"
        type="password"
        class="input input-bordered w-full"
        autocomplete="new-password"
        placeholder="{props.isEditMode ? state.secretMask : ''}"
        value="{state.password}"
        oninput="{(e) => onField('password', e)}"
        required="{!props.isEditMode}"
      />
      <p if="{props.isEditMode && !state.password}" class="text-xs text-gray-600 mt-1 flex items-center gap-1">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_lms_secret_stored_help')}
      </p>
      <p if="{props.isEditMode && state.password}" class="text-xs text-gray-500 mt-1">
        {locale?.t('admin_onpoint_password_replace_help')}
      </p>
    </div>

    <!-- How the credential is handled. Placed after the fields, where it answers the
         question the password box just raised. -->
    <div class="rounded border border-base-300 bg-base-200 p-3">
      <p class="text-sm font-semibold flex items-center gap-2">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_onpoint_credentials_encrypted')}
      </p>
      <p class="text-xs text-gray-600 mt-1">{locale?.t('admin_onpoint_credentials_notice')}</p>
    </div>

    <!-- Reachable customers, populated by a successful test. Read-only here: which
         one a push goes to is chosen when the migration target is created. -->
    <div if="{state.customers.length > 0}" class="rounded border border-base-300 bg-base-200 p-3">
      <p class="text-sm font-semibold">
        {locale?.t('admin_onpoint_customers_found', { count: state.customers.length })}
      </p>
      <ul class="mt-2 flex flex-col gap-1">
        <li each="{c in state.customers}" class="text-sm text-gray-700">
          {c.onpointSliceName} <span class="text-gray-500">({c.onpointSliceId})</span>
        </li>
      </ul>
      <p class="text-xs text-gray-500 mt-2">{locale?.t('admin_onpoint_customers_note')}</p>
    </div>

    <div if="{state.successMessage}" class="alert alert-success text-sm" role="status">
      <i class="icon-check" aria-hidden="true"></i>
      <span>{state.successMessage}</span>
    </div>

    <div if="{state.errorMessage}" class="alert alert-error text-sm" role="alert">
      <i class="icon-error" aria-hidden="true"></i>
      <span>{state.errorMessage}</span>
    </div>
  </div>

  <div class="modal-action">
    <button type="button" class="btn btn-ghost" onclick="{handleCancel}" disabled="{state.busy}"
            aria-label="{locale?.t('cancel')}">
      {locale?.t('cancel')}
    </button>
    <button
      type="button"
      class="btn btn-outline"
      onclick="{handleTest}"
      disabled="{!isValid() || state.busy}"
      aria-label="{locale?.t('admin_onpoint_testconnection')}"
    >
      <span if="{state.busy === 'test'}" class="loading loading-spinner loading-sm"></span>
      {state.busy === 'test' ? locale?.t('testing') : locale?.t('admin_onpoint_testconnection')}
    </button>
    <button
      type="button"
      class="btn btn-primary"
      onclick="{handleSave}"
      disabled="{!isValid() || state.busy}"
      aria-label="{locale?.t('admin_onpoint_save')}"
    >
      <span if="{state.busy === 'save'}" class="loading loading-spinner loading-sm"></span>
      {state.busy === 'save' ? locale?.t('saving') : locale?.t('admin_onpoint_save')}
    </button>
  </div>

  <script>
    import { getRepositoryService } from '../../../services/service-loader';
    import { isCredentialSafeUrl } from '../../../utils/localMachineHost';

    /**
     * What an already-stored secret looks like in the box. A placeholder only — see the
     * password input. Eight bullets rather than the real length, which is not ours to leak.
     */
    const SECRET_MASK = '••••••••';

    export default {
      state: {
        apiBaseUrl: '',
        username: '',
        // What the lookup returned, kept so a retyped account name can be told apart
        // from the stored one — see handleTest.
        storedUsername: '',
        password: '',
        secretMask: SECRET_MASK,
        customers: [],
        successMessage: '',
        errorMessage: '',
        // 'test' | 'save' | null — which action is in flight, so each button shows
        // its own spinner rather than both.
        busy: null
      },

      onMounted() {
        // Re-credentialing an existing connection prefills the URL it was created with.
        // The password is never returned by the API, so it is always either retyped or
        // left blank to keep the stored one.
        this.update({
          apiBaseUrl: this.props.apiBaseUrl || '',
          username: this.props.username || '',
          storedUsername: '',
          password: '',
          customers: [],
          successMessage: '',
          errorMessage: '',
          busy: null
        });
        this.loadStoredUsername();
      },

      /**
       * Fill in the account name this connection was configured with (METALARK-1166).
       *
       * Unlike the password this is not a secret, and an administrator reopening a
       * connection needs to be able to confirm which API user it was set up as. It is
       * fetched rather than handed down as a prop because it lives in the encrypted
       * credential, not on the repository row the list already holds.
       *
       * Best-effort by design: a failure leaves the box empty behind its "a value is
       * already stored" placeholder, which is exactly how the form behaved before, and
       * Save still keeps the stored username when the box is left blank.
       */
      async loadStoredUsername() {
        if (!this.props.isEditMode || !this.props.repositoryId) return;
        // Already supplied by the caller — nothing to look up.
        if (this.state.username) return;
        try {
          const service = await getRepositoryService();
          // skipLoading: a background prefill should not raise the global spinner.
          const result = await service.getOnPointUsername(this.props.repositoryId, true);
          const username = result?.username || '';
          // The admin may have started typing while this was in flight; a late answer
          // must not overwrite what they entered.
          if (username && !this.state.username) this.update({ username, storedUsername: username });
        } catch (error) {
          // Silent on purpose: this can only ever improve on an empty box, so a failure
          // is not worth an error banner on a form that is otherwise usable.
        }
      },

      isValid() {
        const { apiBaseUrl, username, password } = this.state;
        if (!apiBaseUrl?.trim()) return false;
        // A blank username or password is valid ONLY when editing, where each means "keep
        // the stored one". On a create there is nothing to keep. The username is normally
        // prefilled on an edit, so blank there means the lookup found nothing — not that
        // the admin has to remember and retype it.
        if (!this.props.isEditMode && (!username?.trim() || !password)) return false;
        // Credentials ride on every request, so plaintext is refused up front rather than at
        // save time. Loopback and Docker's host alias are exempt, matching what the server
        // accepts: this check used to be stricter than `OnPointProvider.validateLocation`,
        // which silently made a local-stack instance unconfigurable through the UI with no
        // message saying why, only a Save button that never enabled.
        return isCredentialSafeUrl(apiBaseUrl);
      },

      onField(key, e) {
        // Editing invalidates any prior test result, so a stale "connected" message
        // can't imply the current values were verified.
        this.update({
          [key]: e.target.value,
          successMessage: '',
          errorMessage: '',
          customers: []
        });
      },

      payload() {
        const body = { apiBaseUrl: this.state.apiBaseUrl.trim() };
        // Each half omitted rather than sent empty: the server reads an absent value on an
        // update as "keep the stored one", and an empty string would be a credential of
        // length zero.
        const username = this.state.username.trim();
        if (username) body.username = username;
        if (this.state.password) body.password = this.state.password;
        return body;
      },

      async handleTest() {
        if (!this.isValid()) return;
        this.update({ busy: 'test', successMessage: '', errorMessage: '', customers: [] });
        try {
          const service = await getRepositoryService();
          const body = this.payload();
          // The stored-credential probe is only honest while nothing has been retyped.
          // Now that the username box is prefilled (METALARK-1166), editing it must not
          // fall through to "probe what is stored" — that would report the OLD account
          // as connected while the box shows a new one. A changed account name is tested
          // as a new credential, which asks for the password that goes with it.
          const retypedUsername = this.state.username.trim() !== (this.state.storedUsername || '');
          const result = (body.password || retypedUsername)
            ? await service.testOnPointConnection(body)
            : await service.getOnPointCustomers(this.props.repositoryId);
          this.update({
            busy: null,
            customers: result?.customers || [],
            successMessage: this.locale?.t('admin_onpoint_test_success') || 'Connected'
          });
        } catch (error) {
          this.update({ busy: null, errorMessage: this.messageFor(error) });
        }
      },

      async handleSave() {
        if (!this.isValid()) return;
        this.update({ busy: 'save', successMessage: '', errorMessage: '' });
        try {
          const service = await getRepositoryService();
          const body = this.payload();
          if (this.props.repositoryId) body.repositoryId = this.props.repositoryId;
          const result = await service.connectOnPoint(body);
          this.update({ busy: null, customers: result?.customers || [] });
          if (this.props.onsaved) this.props.onsaved(result?.repository);
        } catch (error) {
          this.update({ busy: null, errorMessage: this.messageFor(error) });
        }
      },

      /**
       * The API's own message is the useful one here — it distinguishes a rejected
       * OnPoint credential from an unreachable host from an account lacking the
       * super-admin role, and a generic fallback would discard that.
       */
      messageFor(error) {
        return error?.response?.data?.error?.message
          || error?.response?.data?.message
          || error?.message
          || this.locale?.t('admin_onpoint_test_failed')
          || 'Connection failed';
      },

      handleCancel() {
        if (this.props.oncancel) this.props.oncancel();
      }
    }
  </script>
</onpoint-credentials-form>
